import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, Shield, Zap, Check, ArrowRight, AlertTriangle, Target,
  BarChart2, Award, ExternalLink, CheckCircle, Server, LineChart, Bot,
  Play, Lock, RefreshCw, Headphones, Gift, Building2, Briefcase
} from 'lucide-react';
import { ParticlesBackground } from '../components/ParticlesBackground';
import { InsideBarSubscribeModal } from '../components/InsideBarSubscribeModal';

// TradingView Backtest Screenshots - CORRECTLY MAPPED
const SCREENSHOTS = {
  equityCurve: "https://customer-assets-0z36b82j.emergentagent.net/job_60ef5a15-9d39-4076-a479-d4706fc080a7/artifacts/os4goaa6_image.png",  // Performance Overview with equity curve
  returnDetails: "https://customer-assets-0z36b82j.emergentagent.net/job_60ef5a15-9d39-4076-a479-d4706fc080a7/artifacts/r4muoag5_image.png", // Return Details with strategy vs buy & hold
  drawdowns: "https://customer-assets-0z36b82j.emergentagent.net/job_60ef5a15-9d39-4076-a479-d4706fc080a7/artifacts/b6pr1eqx_image.png",    // Equity Run-ups and Drawdowns
  tradesAnalysis: "https://customer-assets-0z36b82j.emergentagent.net/job_60ef5a15-9d39-4076-a479-d4706fc080a7/artifacts/qx8qquwt_image.png" // Trades Analysis with win/loss distribution
};

// Live Trade Screenshot - Cropped version without sensitive parameters
const LIVE_TRADE_SCREENSHOT = "https://customer-assets-0z36b82j.emergentagent.net/job_60ef5a15-9d39-4076-a479-d4706fc080a7/artifacts/sp8i03r0_image.png";

// Updated Performance Stats from Screenshots
const PERFORMANCE_STATS = {
  totalReturn: 107.44,
  totalPnL: 10743.99,
  maxDrawdown: 8.01,
  maxDrawdownUSD: 1383.25,
  profitableTrades: 63.29,
  winningTrades: 181,
  losingTrades: 105,
  totalTrades: 286,
  profitFactor: 2.289,
  sharpeRatio: 1.207,
  expectedPayoff: 37.57,
  strategyOutperformance: 89.70,
  avgRunUpDuration: 10,
  avgDrawdownDuration: 6,
  maxDrawdownPercent: 13.83,
  largestProfit: 608.56,
  largestLoss: 319.44,
  avgBarsInTrade: 16
};

const fadeUp = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5 } };
const stagger = { animate: { transition: { staggerChildren: 0.1 } } };

export default function InsideBarPage() {
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [activeScreenshot, setActiveScreenshot] = useState('equityCurve');

  const handleSubscribe = () => setShowSubscribeModal(true);

  const ecosystemFeatures = [
    { 
      icon: Bot, 
      title: 'MT5 Expert Advisor', 
      description: 'Fully automated trading on MetaTrader 5. Set it up once and let it trade 24/5.',
      color: '#007AFF'
    },
    { 
      icon: LineChart, 
      title: 'TradingView Pine Strategy', 
      description: 'Complete Pine Script strategy with entry/exit signals, backtesting, and alerts.',
      color: '#8B5CF6'
    },
    { 
      icon: Target, 
      title: 'TradingView Indicator', 
      description: 'Visual indicator overlay for manual traders who prefer discretionary execution.',
      color: '#10B981'
    },
  ];

  const includedFeatures = [
    { icon: Bot, text: 'MT5 Expert Advisor' },
    { icon: LineChart, text: 'TradingView Pine Strategy' },
    { icon: Target, text: 'TradingView Indicator' },
    { icon: RefreshCw, text: 'Continuous Updates' },
    { icon: Gift, text: 'Future Improvements' },
    { icon: Headphones, text: 'Priority Support' },
  ];

  const trustMetrics = [
    { value: '107.44%', label: 'Total Return', sublabel: '365-Day Backtest' },
    { value: '63.29%', label: 'Win Rate', sublabel: '181 of 286 Trades' },
    { value: '2.289', label: 'Profit Factor', sublabel: 'Risk-Adjusted' },
    { value: '8.01%', label: 'Max Drawdown', sublabel: 'Capital Protected' },
  ];

  const screenshotTabs = [
    { id: 'equityCurve', label: 'Equity Curve', icon: TrendingUp },
    { id: 'returnDetails', label: 'Returns', icon: BarChart2 },
    { id: 'drawdowns', label: 'Drawdowns', icon: Shield },
    { id: 'tradesAnalysis', label: 'Trade Analysis', icon: Target },
  ];

  return (
    <div className="min-h-screen bg-[#050508]">
      {/* Hero Section - Completely Redesigned */}
      <section className="relative pt-24 pb-16 overflow-hidden" data-testid="ib-hero-section">
        <div className="absolute inset-0 z-0"><ParticlesBackground /></div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#8B5CF6]/5 via-transparent to-transparent" />
        
        <div className="relative z-10 max-w-6xl mx-auto px-6">
          {/* Trust Badge */}
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass border border-[#10B981]/30">
              <div className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
              <span className="text-[#10B981] text-xs font-medium">Verified 365-Day Backtest Results</span>
              <CheckCircle size={14} className="text-[#10B981]" />
            </div>
          </motion.div>

          {/* Main Hero Content */}
          <motion.div initial="initial" animate="animate" variants={stagger} className="text-center max-w-4xl mx-auto">
            <motion.h1 variants={fadeUp} className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-white mb-4 leading-tight">
              The Complete{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B5CF6] via-[#007AFF] to-[#10B981]">
                Algorithmic Trading
              </span>
              {' '}System
            </motion.h1>
            
            <motion.p variants={fadeUp} className="text-lg md:text-xl text-white/60 mb-6 max-w-2xl mx-auto">
              MT5 Expert Advisor + TradingView Strategy + Indicator — all in one subscription. 
              Professionally backtested on <span className="text-[#F59E0B] font-semibold">XAUUSD</span> with verified results.
            </motion.p>

            {/* Key Stats Row */}
            <motion.div variants={fadeUp} className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8 max-w-3xl mx-auto">
              {trustMetrics.map((metric, i) => (
                <div key={i} className="glass rounded-xl p-4 text-center">
                  <div className="font-heading font-bold text-2xl md:text-3xl text-[#10B981]">{metric.value}</div>
                  <div className="text-white/70 text-xs font-medium">{metric.label}</div>
                  <div className="text-white/40 text-[10px]">{metric.sublabel}</div>
                </div>
              ))}
            </motion.div>

            {/* CTA Buttons */}
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8">
              <button 
                onClick={handleSubscribe}
                className="w-full sm:w-auto bg-gradient-to-r from-[#8B5CF6] to-[#10B981] hover:from-[#9B6CF6] hover:to-[#1AB981] text-white font-heading font-semibold text-base px-8 py-4 rounded-xl flex items-center justify-center gap-2 group transition-all shadow-lg shadow-[#8B5CF6]/25"
                data-testid="hero-subscribe-btn"
              >
                <Zap size={18} className="group-hover:animate-pulse" />
                Get Complete Access — ₹9,990/mo
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <a href="#backtest-results" className="w-full sm:w-auto btn-secondary flex items-center justify-center gap-2">
                <Play size={16} /> View Backtest Results
              </a>
            </motion.div>

            {/* What's Included Pills */}
            <motion.div variants={fadeUp} className="flex flex-wrap items-center justify-center gap-2">
              {includedFeatures.slice(0, 3).map((feature, i) => (
                <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                  <feature.icon size={14} className="text-[#8B5CF6]" />
                  <span className="text-white/70 text-xs">{feature.text}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Social Proof Bar */}
      <section className="py-4 border-y border-white/5 bg-white/[0.02]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-12 text-center">
            <div className="flex items-center gap-2">
              <TrendingUp size={16} className="text-[#10B981]" />
              <span className="text-white/60 text-sm">+$10,743 Net Profit</span>
            </div>
            <div className="flex items-center gap-2">
              <Target size={16} className="text-[#007AFF]" />
              <span className="text-white/60 text-sm">286 Verified Trades</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield size={16} className="text-[#8B5CF6]" />
              <span className="text-white/60 text-sm">Deep Backtesting</span>
            </div>
            <div className="flex items-center gap-2">
              <Award size={16} className="text-[#F59E0B]" />
              <span className="text-white/60 text-sm">TradingView Verified</span>
            </div>
          </div>
        </div>
      </section>

      {/* Complete Ecosystem Section */}
      <section className="py-16" id="ecosystem" data-testid="ecosystem-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full glass text-[#8B5CF6] text-xs font-mono mb-4">
              ONE SUBSCRIPTION • COMPLETE ACCESS
            </span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              Everything You Need to Trade Professionally
            </h2>
            <p className="text-white/50 text-lg max-w-2xl mx-auto">
              Get the complete algorithmic trading ecosystem — MT5 automation, TradingView strategy, and visual indicators all working together.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {ecosystemFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass rounded-2xl p-6 border border-white/5 hover:border-white/10 transition-all group"
              >
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-4`} style={{ backgroundColor: `${feature.color}15` }}>
                  <feature.icon className="w-7 h-7" style={{ color: feature.color }} />
                </div>
                <h3 className="font-heading font-semibold text-xl text-white mb-2">{feature.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed mb-4">{feature.description}</p>
                <div className="flex items-center gap-2 text-xs" style={{ color: feature.color }}>
                  <Check size={14} /> Included in subscription
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TradingView Backtest Results - Real Screenshots */}
      <section className="py-16 section-glow" id="backtest-results" data-testid="backtest-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 rounded-full glass text-[#10B981] text-xs font-mono mb-4">
              VERIFIED TRADINGVIEW BACKTEST
            </span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              Real Strategy Tester Results
            </h2>
            <p className="text-white/50 text-lg max-w-2xl mx-auto">
              365-day deep backtesting on XAUUSD. These are actual TradingView Strategy Tester screenshots — no simulations, no hypotheticals.
            </p>
          </motion.div>

          {/* Screenshot Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            {screenshotTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveScreenshot(tab.id)}
                data-testid={`backtest-tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeScreenshot === tab.id
                    ? 'bg-[#8B5CF6] text-white'
                    : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                }`}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Screenshot Display */}
          <motion.div 
            key={activeScreenshot}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl"
          >
            <div className="absolute top-0 left-0 right-0 h-8 bg-[#1a1a2e] flex items-center px-3 gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-500/80" />
              <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
              <div className="w-3 h-3 rounded-full bg-green-500/80" />
              <span className="ml-3 text-white/40 text-xs">TradingView Strategy Tester — MA-IB XAUUSD</span>
            </div>
            <img 
              src={SCREENSHOTS[activeScreenshot]} 
              alt={`TradingView ${activeScreenshot} screenshot`}
              className="w-full pt-8"
              loading="lazy"
              data-testid="backtest-screenshot"
            />
          </motion.div>

          {/* Key Metrics from Screenshots */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <div className="glass rounded-xl p-4 text-center">
              <div className="text-[#10B981] font-heading font-bold text-2xl">+${PERFORMANCE_STATS.totalPnL.toLocaleString()}</div>
              <div className="text-white/50 text-xs">Total Net Profit</div>
            </div>
            <div className="glass rounded-xl p-4 text-center">
              <div className="text-[#007AFF] font-heading font-bold text-2xl">{PERFORMANCE_STATS.profitableTrades}%</div>
              <div className="text-white/50 text-xs">Win Rate ({PERFORMANCE_STATS.winningTrades}/{PERFORMANCE_STATS.totalTrades})</div>
            </div>
            <div className="glass rounded-xl p-4 text-center">
              <div className="text-[#8B5CF6] font-heading font-bold text-2xl">{PERFORMANCE_STATS.profitFactor}</div>
              <div className="text-white/50 text-xs">Profit Factor</div>
            </div>
            <div className="glass rounded-xl p-4 text-center">
              <div className="text-[#F59E0B] font-heading font-bold text-2xl">{PERFORMANCE_STATS.maxDrawdown}%</div>
              <div className="text-white/50 text-xs">Max Drawdown</div>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Performance Metrics */}
      <section className="py-16" data-testid="metrics-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 rounded-full glass text-[#007AFF] text-xs font-mono mb-4">
              PERFORMANCE BREAKDOWN
            </span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              Transparent Performance Metrics
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Returns Card */}
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="glass rounded-2xl p-6 border border-white/5">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-[#10B981]/15 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-[#10B981]" />
                </div>
                <h3 className="font-heading font-semibold text-white text-lg">Return Statistics</h3>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-white/50 text-sm">Total Return</span>
                  <span className="text-[#10B981] font-mono font-semibold">+{PERFORMANCE_STATS.totalReturn}%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/50 text-sm">Strategy Outperformance</span>
                  <span className="text-[#10B981] font-mono font-semibold">+{PERFORMANCE_STATS.strategyOutperformance}%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/50 text-sm">Expected Payoff</span>
                  <span className="text-white font-mono">${PERFORMANCE_STATS.expectedPayoff}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/50 text-sm">Sharpe Ratio</span>
                  <span className="text-white font-mono">{PERFORMANCE_STATS.sharpeRatio}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/50 text-sm">Profit Factor</span>
                  <span className="text-[#8B5CF6] font-mono font-semibold">{PERFORMANCE_STATS.profitFactor}</span>
                </div>
              </div>
            </motion.div>

            {/* Risk Card */}
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="glass rounded-2xl p-6 border border-white/5">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-[#F59E0B]/15 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-[#F59E0B]" />
                </div>
                <h3 className="font-heading font-semibold text-white text-lg">Risk Statistics</h3>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-white/50 text-sm">Max Drawdown</span>
                  <span className="text-[#F59E0B] font-mono font-semibold">{PERFORMANCE_STATS.maxDrawdown}%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/50 text-sm">Max DD (USD)</span>
                  <span className="text-white font-mono">${PERFORMANCE_STATS.maxDrawdownUSD.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/50 text-sm">Avg Drawdown Duration</span>
                  <span className="text-white font-mono">{PERFORMANCE_STATS.avgDrawdownDuration} days</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/50 text-sm">Largest Loss</span>
                  <span className="text-red-400 font-mono">-${PERFORMANCE_STATS.largestLoss}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/50 text-sm">Largest Profit</span>
                  <span className="text-[#10B981] font-mono">+${PERFORMANCE_STATS.largestProfit}</span>
                </div>
              </div>
            </motion.div>

            {/* Trade Statistics */}
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="glass rounded-2xl p-6 border border-white/5">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-[#007AFF]/15 flex items-center justify-center">
                  <BarChart2 className="w-5 h-5 text-[#007AFF]" />
                </div>
                <h3 className="font-heading font-semibold text-white text-lg">Trade Statistics</h3>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-white/50 text-sm">Total Trades</span>
                  <span className="text-white font-mono font-semibold">{PERFORMANCE_STATS.totalTrades}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/50 text-sm">Winning Trades</span>
                  <span className="text-[#10B981] font-mono">{PERFORMANCE_STATS.winningTrades} ({PERFORMANCE_STATS.profitableTrades}%)</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/50 text-sm">Losing Trades</span>
                  <span className="text-red-400 font-mono">{PERFORMANCE_STATS.losingTrades} ({(100 - PERFORMANCE_STATS.profitableTrades).toFixed(2)}%)</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/50 text-sm">Avg Bars in Trade</span>
                  <span className="text-white font-mono">{PERFORMANCE_STATS.avgBarsInTrade}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/50 text-sm">Avg Run-Up Duration</span>
                  <span className="text-white font-mono">{PERFORMANCE_STATS.avgRunUpDuration} days</span>
                </div>
              </div>
            </motion.div>

            {/* Configuration */}
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="glass rounded-2xl p-6 border border-white/5">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-[#8B5CF6]/15 flex items-center justify-center">
                  <Server className="w-5 h-5 text-[#8B5CF6]" />
                </div>
                <h3 className="font-heading font-semibold text-white text-lg">Backtest Configuration</h3>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-white/50 text-sm">Symbol</span>
                  <span className="text-[#F59E0B] font-mono font-semibold">XAUUSD</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/50 text-sm">Timeframe</span>
                  <span className="text-white font-mono">5 Minutes</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/50 text-sm">Test Period</span>
                  <span className="text-white font-mono">365 Days (Deep)</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/50 text-sm">Initial Capital</span>
                  <span className="text-white font-mono">$10,000 USD</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/50 text-sm">Platform</span>
                  <span className="text-[#8B5CF6] font-mono">TradingView</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Prop Firm Compatible Section - USP */}
      <section className="py-20 relative overflow-hidden" data-testid="prop-firm-section">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#F59E0B]/10 via-transparent to-[#8B5CF6]/10" />
        
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-[#F59E0B]/20 to-[#8B5CF6]/20 border border-[#F59E0B]/30 text-[#F59E0B] text-xs font-mono mb-4">
              <Building2 size={14} />
              UNIQUE SELLING POINT
            </span>
            <h2 className="font-heading font-bold text-3xl md:text-5xl text-white mb-4">
              Prop Firm <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#8B5CF6]">Compatible</span>
            </h2>
            <p className="text-white/60 text-lg max-w-3xl mx-auto">
              Designed specifically for traders targeting funded accounts. Our strategy meets the strict requirements of both CFD and Futures proprietary trading firms.
            </p>
          </motion.div>

          {/* Two Cards: CFD & Futures */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {/* CFD Prop Firms */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative rounded-2xl overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#F59E0B]/20 to-transparent" />
              <div className="glass border border-[#F59E0B]/20 rounded-2xl p-8 relative">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-xl bg-[#F59E0B]/15 flex items-center justify-center">
                    <BarChart2 className="w-7 h-7 text-[#F59E0B]" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-2xl text-white">CFD Prop Firms</h3>
                    <p className="text-[#F59E0B] text-sm font-medium">Forex & Metals Trading</p>
                  </div>
                </div>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] flex-shrink-0" />
                    <span className="text-white/80">Low drawdown under 10% threshold</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] flex-shrink-0" />
                    <span className="text-white/80">Consistent daily/weekly profits</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] flex-shrink-0" />
                    <span className="text-white/80">No martingale or grid trading</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] flex-shrink-0" />
                    <span className="text-white/80">Risk management built-in</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {['FTMO', 'The5ers', 'MyForexFunds', 'E8 Funding'].map((firm) => (
                    <span key={firm} className="px-3 py-1 rounded-full bg-[#F59E0B]/10 text-[#F59E0B] text-xs font-medium border border-[#F59E0B]/20">
                      {firm}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Futures Prop Firms */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative rounded-2xl overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#8B5CF6]/20 to-transparent" />
              <div className="glass border border-[#8B5CF6]/20 rounded-2xl p-8 relative">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-xl bg-[#8B5CF6]/15 flex items-center justify-center">
                    <Briefcase className="w-7 h-7 text-[#8B5CF6]" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-2xl text-white">Futures Prop Firms</h3>
                    <p className="text-[#8B5CF6] text-sm font-medium">Index & Commodity Futures</p>
                  </div>
                </div>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] flex-shrink-0" />
                    <span className="text-white/80">Passes evaluation challenges</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] flex-shrink-0" />
                    <span className="text-white/80">Meets profit targets consistently</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] flex-shrink-0" />
                    <span className="text-white/80">Stays within trailing drawdown</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] flex-shrink-0" />
                    <span className="text-white/80">No prohibited strategies</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {['Topstep', 'Apex Trader', 'Earn2Trade', 'Leeloo Trading'].map((firm) => (
                    <span key={firm} className="px-3 py-1 rounded-full bg-[#8B5CF6]/10 text-[#8B5CF6] text-xs font-medium border border-[#8B5CF6]/20">
                      {firm}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Key Stats for Prop Trading */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
          >
            <div className="glass rounded-xl p-5 text-center border border-white/5">
              <div className="text-[#10B981] font-heading font-bold text-3xl mb-1">8.01%</div>
              <div className="text-white/50 text-sm">Max Drawdown</div>
              <div className="text-[#10B981] text-xs mt-1">✓ Under 10% limit</div>
            </div>
            <div className="glass rounded-xl p-5 text-center border border-white/5">
              <div className="text-[#F59E0B] font-heading font-bold text-3xl mb-1">63.29%</div>
              <div className="text-white/50 text-sm">Win Rate</div>
              <div className="text-[#F59E0B] text-xs mt-1">✓ Consistent profits</div>
            </div>
            <div className="glass rounded-xl p-5 text-center border border-white/5">
              <div className="text-[#8B5CF6] font-heading font-bold text-3xl mb-1">2.289</div>
              <div className="text-white/50 text-sm">Profit Factor</div>
              <div className="text-[#8B5CF6] text-xs mt-1">✓ Strong edge</div>
            </div>
            <div className="glass rounded-xl p-5 text-center border border-white/5">
              <div className="text-[#007AFF] font-heading font-bold text-3xl mb-1">12/13</div>
              <div className="text-white/50 text-sm">Profitable Months</div>
              <div className="text-[#007AFF] text-xs mt-1">✓ Reliable income</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why This Strategy Works */}
      <section className="py-16" data-testid="why-strategy-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full glass text-[#10B981] text-xs font-mono mb-4">
              WHY THIS STRATEGY
            </span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              Built for Steady, Consistent Returns
            </h2>
            <p className="text-white/50 text-lg max-w-2xl mx-auto">
              Not about big swings or gambling. This is a professional trading system designed for sustainable wealth building through disciplined, repeatable execution.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Low Drawdown', desc: 'Maximum drawdown of just 8.01% means your capital is protected while compounding grows your wealth steadily.' },
              { step: '02', title: 'Consistent Wins', desc: '63.29% win rate with a 2.289 profit factor. More winning trades than losing, and winners are bigger than losers.' },
              { step: '03', title: 'Compound Growth', desc: '107.44% return in 365 days. Steady monthly gains compound into significant annual returns.' },
              { step: '04', title: 'Verified Results', desc: 'Every trade and metric verified through TradingView Strategy Tester. No hypotheticals, no simulations.' },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#10B981] to-[#007AFF] flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-heading font-bold text-lg">{item.step}</span>
                </div>
                <h3 className="font-heading font-semibold text-white text-lg mb-2">{item.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Monthly Performance */}
      <section className="py-16 bg-white/[0.02]" data-testid="monthly-performance-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 rounded-full glass text-[#007AFF] text-xs font-mono mb-4">
              365-DAY BACKTEST
            </span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              Month-by-Month Performance
            </h2>
            <p className="text-white/50 text-lg max-w-2xl mx-auto">
              Actual monthly PnL from TradingView Strategy Tester. Starting capital: $10,000 USD.
            </p>
          </motion.div>

          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            {[
              { month: "Jul '25", pnl: '+$753', positive: true },
              { month: "Aug '25", pnl: '+$1,131', positive: true },
              { month: "Sep '25", pnl: '+$2,196', positive: true },
              { month: "Oct '25", pnl: '+$893', positive: true },
              { month: "Nov '25", pnl: '+$2,035', positive: true },
              { month: "Dec '25", pnl: '+$896', positive: true },
              { month: "Jan '26", pnl: '+$619', positive: true },
              { month: "Feb '26", pnl: '+$1,076', positive: true },
              { month: "Mar '26", pnl: '+$1,131', positive: true },
              { month: "Apr '26", pnl: '+$666', positive: true },
              { month: "May '26", pnl: '+$530', positive: true },
              { month: "Jun '26", pnl: '-$183', positive: false },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className={`glass rounded-xl p-4 text-center border ${item.positive ? 'border-[#10B981]/20' : 'border-red-500/20'}`}
              >
                <div className="text-white/40 text-xs font-mono mb-1">{item.month}</div>
                <div className={`font-heading font-bold text-lg ${item.positive ? 'text-[#10B981]' : 'text-red-400'}`}>
                  {item.pnl}
                </div>
              </motion.div>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <div className="glass rounded-xl p-4 text-center">
              <div className="text-[#10B981] font-heading font-bold text-2xl">11/12</div>
              <div className="text-white/50 text-xs">Profitable Months</div>
            </div>
            <div className="glass rounded-xl p-4 text-center">
              <div className="text-[#007AFF] font-heading font-bold text-2xl">+$10,744</div>
              <div className="text-white/50 text-xs">Total Net Profit</div>
            </div>
            <div className="glass rounded-xl p-4 text-center">
              <div className="text-[#8B5CF6] font-heading font-bold text-2xl">+$2,196</div>
              <div className="text-white/50 text-xs">Best Month (Sep)</div>
            </div>
            <div className="glass rounded-xl p-4 text-center">
              <div className="text-red-400 font-heading font-bold text-2xl">-$183</div>
              <div className="text-white/50 text-xs">Worst Month (Jun)</div>
            </div>
          </div>
        </div>
      </section>

      {/* Live Trade Example - Without revealing sensitive data */}
      <section className="py-16" data-testid="live-trade-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 rounded-full glass text-[#F59E0B] text-xs font-mono mb-4">
              LIVE TRADE EXAMPLE
            </span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              Real Execution on XAUUSD
            </h2>
            <p className="text-white/50 text-lg max-w-2xl mx-auto">
              A real Inside Bar trade execution showing precise entry and profitable exit. This is how the strategy performs in live market conditions.
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-[#131722]"
          >
            {/* Browser-style title bar */}
            <div className="absolute top-0 left-0 right-0 h-8 bg-[#1a1a2e] flex items-center px-3 gap-1.5 z-20">
              <div className="w-3 h-3 rounded-full bg-red-500/80" />
              <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
              <div className="w-3 h-3 rounded-full bg-green-500/80" />
              <span className="ml-3 text-white/40 text-xs">TradingView — XAUUSD — Live Trade</span>
            </div>
            
            {/* Cropped image - no gradient needed */}
            <img 
              src={LIVE_TRADE_SCREENSHOT} 
              alt="Live Inside Bar trade on XAUUSD"
              className="w-full pt-8"
              loading="lazy"
              data-testid="live-trade-screenshot"
            />
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <div className="glass rounded-xl p-4 text-center">
              <div className="text-[#F59E0B] font-heading font-bold text-lg">Inside Bar</div>
              <div className="text-white/50 text-xs">Pattern Detected</div>
            </div>
            <div className="glass rounded-xl p-4 text-center">
              <div className="text-red-400 font-heading font-bold text-lg">Short Entry</div>
              <div className="text-white/50 text-xs">Signal Triggered</div>
            </div>
            <div className="glass rounded-xl p-4 text-center">
              <div className="text-[#10B981] font-heading font-bold text-lg">Take Profit</div>
              <div className="text-white/50 text-xs">Target Hit</div>
            </div>
            <div className="glass rounded-xl p-4 text-center">
              <div className="text-[#8B5CF6] font-heading font-bold text-lg">Profitable</div>
              <div className="text-white/50 text-xs">Trade Outcome</div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section - Redesigned */}
      <section className="py-16 section-glow" id="pricing" data-testid="pricing-section">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, scale: 0.98 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
            <div className="relative rounded-3xl overflow-hidden border border-[#8B5CF6]/30">
              <div className="absolute inset-0 bg-gradient-to-br from-[#8B5CF6]/10 via-[#0a0a12] to-[#10B981]/10" />
              
              <div className="relative z-10 p-8 md:p-12">
                <div className="grid md:grid-cols-2 gap-10 items-center">
                  {/* Left - Pricing */}
                  <div className="text-center md:text-left">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#8B5CF6]/20 to-[#10B981]/20 border border-[#8B5CF6]/30 mb-6">
                      <Zap size={14} className="text-[#8B5CF6]" />
                      <span className="text-white text-xs font-semibold">COMPLETE TRADING SYSTEM</span>
                    </div>

                    <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-3">
                      Get Full Access Today
                    </h2>
                    <p className="text-white/50 mb-6">
                      One subscription. Complete ecosystem. MT5 + TradingView + Indicator.
                    </p>
                    
                    <div className="flex items-baseline justify-center md:justify-start gap-2 mb-6">
                      <span className="font-heading font-bold text-5xl md:text-6xl text-transparent bg-clip-text bg-gradient-to-r from-[#8B5CF6] via-[#10B981] to-[#007AFF]">
                        ₹9,990
                      </span>
                      <div className="flex flex-col items-start">
                        <span className="text-white/40 text-sm">/month</span>
                        <span className="text-[#10B981] text-[10px] font-mono">Cancel anytime</span>
                      </div>
                    </div>

                    <button 
                      onClick={handleSubscribe}
                      className="w-full md:w-auto bg-gradient-to-r from-[#8B5CF6] to-[#10B981] hover:from-[#9B6CF6] hover:to-[#1AB981] text-white font-heading font-semibold text-lg px-10 py-4 rounded-xl flex items-center justify-center gap-3 group transition-all shadow-lg shadow-[#8B5CF6]/25"
                      data-testid="pricing-subscribe-btn"
                    >
                      <Zap size={20} className="group-hover:animate-pulse" />
                      Start Trading Now
                      <ExternalLink size={16} className="opacity-70" />
                    </button>
                    
                    <p className="text-white/30 text-xs mt-4">
                      Secure payment via Razorpay • Instant access after payment
                    </p>
                  </div>

                  {/* Right - What's Included */}
                  <div className="space-y-4">
                    <h3 className="font-heading font-semibold text-white text-lg mb-4">Everything Included:</h3>
                    {includedFeatures.map((feature, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5">
                        <div className="w-10 h-10 rounded-lg bg-[#10B981]/15 flex items-center justify-center flex-shrink-0">
                          <feature.icon className="w-5 h-5 text-[#10B981]" />
                        </div>
                        <span className="text-white font-medium">{feature.text}</span>
                        <Check size={16} className="text-[#10B981] ml-auto" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust & Guarantee Section */}
      <section className="py-16" data-testid="trust-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="glass rounded-2xl p-6 text-center">
              <div className="w-14 h-14 rounded-full bg-[#10B981]/15 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-7 h-7 text-[#10B981]" />
              </div>
              <h3 className="font-heading font-semibold text-white text-lg mb-2">Verified Results</h3>
              <p className="text-white/50 text-sm">All performance data comes from TradingView&apos;s Strategy Tester. No hypothetical simulations.</p>
            </div>
            <div className="glass rounded-2xl p-6 text-center">
              <div className="w-14 h-14 rounded-full bg-[#8B5CF6]/15 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-7 h-7 text-[#8B5CF6]" />
              </div>
              <h3 className="font-heading font-semibold text-white text-lg mb-2">Secure Access</h3>
              <p className="text-white/50 text-sm">Payment via Razorpay. Instant access to all tools after successful subscription.</p>
            </div>
            <div className="glass rounded-2xl p-6 text-center">
              <div className="w-14 h-14 rounded-full bg-[#007AFF]/15 flex items-center justify-center mx-auto mb-4">
                <Headphones className="w-7 h-7 text-[#007AFF]" />
              </div>
              <h3 className="font-heading font-semibold text-white text-lg mb-2">Priority Support</h3>
              <p className="text-white/50 text-sm">Get help with setup, configuration, and any questions via our dedicated support.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Risk Disclaimer */}
      <section className="py-8 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-start gap-3 p-4 rounded-xl bg-yellow-500/5 border border-yellow-500/20">
            <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-yellow-500 font-semibold text-sm mb-1">Risk Disclaimer</h4>
              <p className="text-white/40 text-xs leading-relaxed">
                Trading forex, CFDs, and other financial instruments involves significant risk of loss. Past performance is not indicative of future results. 
                The backtested results shown are from TradingView Strategy Tester and do not guarantee future performance. Only trade with capital you can afford to lose. 
                This is a tool to assist your trading, not financial advice.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-4">
            Ready to Trade with a Verified System?
          </h2>
          <p className="text-white/50 mb-6">
            Join traders using our complete algorithmic trading ecosystem.
          </p>
          <button 
            onClick={handleSubscribe}
            className="bg-gradient-to-r from-[#8B5CF6] to-[#10B981] text-white font-heading font-semibold px-8 py-4 rounded-xl flex items-center justify-center gap-2 mx-auto group"
          >
            <Zap size={18} /> Get Complete Access — ₹9,990/mo
          </button>
        </div>
      </section>

      {/* Subscribe Modal */}
      <InsideBarSubscribeModal 
        isOpen={showSubscribeModal} 
        onClose={() => setShowSubscribeModal(false)} 
      />
    </div>
  );
}
